import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Theme, alpha } from "@mui/material/styles";
import { SYSTEM_SETTINGS_PATH } from "../SystemSettings/constants";
import { MISSING_DOCUMENT_TEXT } from "./constants";
import { Thresholds, ViewLevel, legendTextOf } from "./incomeExpenseReport";

const SWATCH_SIZE = 12;
const SWATCH_OPACITY = 0.14;
const DOT_SIZE = 7;

const SWATCHES = [
  { palette: "error", label: "เกินเกณฑ์ / ขาดทุน" },
  { palette: "warning", label: "เฝ้าระวัง" },
  { palette: "success", label: "ตามเกณฑ์" },
] as const;

const FOOT_HINT: Record<ViewLevel, string> = {
  branch: "คลิกแว่นขยายที่ชื่อสาขาเพื่อดูระดับถัดไป · ตัวเอียง = ประมาณการ",
  route: "คลิกแว่นขยายที่ชื่อสายเพื่อดูรายวัน · ตัวเอียง = ประมาณการ",
  day: "คลิกแถวเพื่อดูรายละเอียดรายวัน · ตัวเอียง = ประมาณการ",
};

interface ReportLegendProps {
  level: ViewLevel;
  /** null while the settings load, or when they could not be read. */
  thresholds: Thresholds | null;
  isSettingsFailed: boolean;
  showsMissingDocument: boolean;
  /** MASTER edits the thresholds; the link is hidden from everyone else. */
  canOpenSettings: boolean;
}

/** What the colours mean, with the thresholds read live from ตั้งค่าระบบ. */
export default function ReportLegend({
  level,
  thresholds,
  isSettingsFailed,
  showsMissingDocument,
  canOpenSettings,
}: Readonly<ReportLegendProps>) {
  return (
    <Stack gap={0.5}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={0.5}>
        {SWATCHES.map((swatch) => (
          <Stack key={swatch.palette} direction="row" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: SWATCH_SIZE,
                height: SWATCH_SIZE,
                borderRadius: 0.5,
                bgcolor: (theme: Theme) => alpha(theme.palette[swatch.palette].main, SWATCH_OPACITY),
                border: 1,
                borderColor: `${swatch.palette}.main`,
              }}
            />
            <Typography variant="caption">{swatch.label}</Typography>
          </Stack>
        ))}
        {showsMissingDocument && (
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Box
              sx={{ width: DOT_SIZE, height: DOT_SIZE, borderRadius: "50%", bgcolor: "warning.main" }}
            />
            <Typography variant="caption">{MISSING_DOCUMENT_TEXT}</Typography>
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {FOOT_HINT[level]}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
        <Typography variant="caption" fontWeight={600}>
          เกณฑ์ (จากตั้งค่าระบบ):
        </Typography>
        {thresholds && <Typography variant="caption">{legendTextOf(thresholds)}</Typography>}
        {isSettingsFailed && (
          <Typography variant="caption" color="error">
            อ่านเกณฑ์จากตั้งค่าระบบไม่สำเร็จ ตารางจึงยังไม่แสดงสี
          </Typography>
        )}
        {canOpenSettings && (
          <Link component={RouterLink} to={SYSTEM_SETTINGS_PATH} variant="caption">
            ตั้งค่า ›
          </Link>
        )}
      </Stack>
    </Stack>
  );
}
